import * as React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Reveal from './motion/Reveal';

/**
 * A consistent, eye-catching heading used across sections:
 * a small gradient "eyebrow" label + big title + optional subtitle.
 */
export default function SectionHeading({ eyebrow, title, highlight, subtitle, align = 'left', maxWidth = 720 }) {
  const isCenter = align === 'center';
  return (
    <Box
      sx={{
        maxWidth,
        mx: isCenter ? 'auto' : 0,
        textAlign: isCenter ? 'center' : 'left',
      }}
    >
      {eyebrow && (
        <Reveal y={16}>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              px: 1.6,
              py: 0.6,
              mb: 2.5,
              borderRadius: 999,
              border: '1px solid',
              borderColor: 'divider',
              backdropFilter: 'blur(8px)',
              bgcolor: (t) =>
                t.palette.mode === 'dark' ? 'hsla(210, 90%, 60%, 0.08)' : 'hsla(210, 90%, 50%, 0.06)',
            }}
          >
            <Box
              component="span"
              sx={{
                width: 7,
                height: 7,
                borderRadius: '50%',
                background: 'linear-gradient(hsl(210,98%,55%), hsl(265,90%,62%))',
                boxShadow: '0 0 10px hsla(210,98%,55%,0.9)',
              }}
            />
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                letterSpacing: 1.4,
                textTransform: 'uppercase',
                color: 'text.secondary',
              }}
            >
              {eyebrow}
            </Typography>
          </Box>
        </Reveal>
      )}

      <Reveal delay={0.05}>
        <Typography
          component="h2"
          sx={{
            fontFamily: '"Sora", "Inter", sans-serif',
            fontWeight: 800,
            lineHeight: 1.1,
            letterSpacing: -0.5,
            fontSize: { xs: '1.9rem', sm: '2.4rem', md: '2.8rem' },
            color: 'text.primary',
          }}
        >
          {title}{' '}
          {highlight && <Box component="span" className="fx-gradient-text">{highlight}</Box>}
        </Typography>
      </Reveal>

      {subtitle && (
        <Reveal delay={0.12}>
          <Typography
            variant="body1"
            sx={{
              mt: 2,
              fontSize: { xs: '1rem', md: '1.075rem' },
              lineHeight: 1.7,
              color: 'text.secondary',
              mx: isCenter ? 'auto' : 0,
            }}
          >
            {subtitle}
          </Typography>
        </Reveal>
      )}
    </Box>
  );
}
